import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import { memosData } from '../data/memosData';
import './Memos.css';

function MemosList() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Memos';
  }, []);

  const sortedMemos = [...memosData].sort((a, b) => b.date.localeCompare(a.date));

  const formatDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-');
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="memos-page">
      <Header title="Memos" subtitle="Select Board Review" />

      <div className="memos-content">
        <div className="memos-list">
          {sortedMemos.map(memo => (
            <div
              key={memo.id}
              className="memos-item"
              onClick={() => navigate(`/memos/${memo.id}`)}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && navigate(`/memos/${memo.id}`)}
            >
              <div className="memos-item-date">{formatDate(memo.date)}</div>
              <div className="memos-item-title">{memo.title}</div>
              <div className="memos-item-from">{memo.from}</div>
              {memo.department && (
                <div className="memos-item-dept">{memo.department}</div>
              )}
            </div>
          ))}
          {sortedMemos.length === 0 && (
            <div className="memos-empty">No memos available.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function FootnoteRef({ id, footnotes }) {
  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef(null);
  const btnRef = useRef(null);

  const footnote = footnotes.find(f => f.id === id);

  useEffect(() => {
    if (!showPopup) return;
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target) &&
          btnRef.current && !btnRef.current.contains(e.target)) {
        setShowPopup(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showPopup]);

  if (!footnote) return <sup>{id}</sup>;

  return (
    <span className="memo-footnote-wrapper">
      <sup
        ref={btnRef}
        className="memo-footnote-ref"
        onClick={(e) => { e.stopPropagation(); setShowPopup(!showPopup); }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setShowPopup(!showPopup)}
        aria-label={`Footnote ${id}`}
      >
        {id}
      </sup>
      {showPopup && (
        <span className="memo-footnote-popup" ref={popupRef}>
          <span className="memo-footnote-popup-header">
            <span className="memo-footnote-popup-num">Note {id}</span>
            <button
              className="memo-footnote-popup-close"
              onClick={(e) => { e.stopPropagation(); setShowPopup(false); }}
              aria-label="Close"
            >×</button>
          </span>
          <span className="memo-footnote-popup-text">
            {footnote.url ? (
              <a href={footnote.url} target="_blank" rel="noopener noreferrer">{footnote.text}</a>
            ) : (
              footnote.text
            )}
          </span>
        </span>
      )}
    </span>
  );
}

function renderContent(text, footnotes) {
  // Split on {{N}} footnote markers
  const parts = text.split(/\{\{(\d+)\}\}/);
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      // This is a footnote number
      return <FootnoteRef key={i} id={parseInt(part)} footnotes={footnotes} />;
    }
    return part;
  });
}

function MemoViewer() {
  const { memoId } = useParams();
  const navigate = useNavigate();

  const memo = memosData.find(m => m.id === memoId);

  useEffect(() => {
    if (memo) {
      document.title = `${memo.title} - Memos`;
    }
  }, [memo]);

  const formatDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-');
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (!memo) {
    return (
      <div className="memos-page">
        <Header title="Memos" />
        <div className="memos-content">
          <div className="memos-not-found">
            <h2>Memo not found</h2>
            <Link to="/memos">Back to all memos</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="memos-page">
      <Header title="Memos" subtitle="Select Board Review" />

      <div className="memos-content">
        <button className="memos-back" onClick={() => navigate('/memos')}>
          ← Back to all memos
        </button>

        <div className="memo-document">
          <div className="memo-letterhead">
            <div className="memo-dept-name">{memo.departmentFull || memo.department}</div>
            <div className="memo-dept-contact">
              <span>Kara Brewton, Director</span>
              <span>Town Hall, 3rd Floor</span>
              <span>333 Washington St.</span>
              <span>Brookline, MA 02245</span>
              <span>617-730-2130</span>
            </div>
          </div>

          <div className="memo-header-fields">
            <h1 className="memo-title-heading">Memorandum</h1>
            <div className="memo-field"><span className="memo-field-label">To:</span> {memo.to}</div>
            <div className="memo-field"><span className="memo-field-label">From:</span> {memo.from}</div>
            <div className="memo-field"><span className="memo-field-label">Date:</span> {formatDate(memo.date)}</div>
            <div className="memo-field"><span className="memo-field-label">Re:</span> {memo.title}</div>
            {memo.cc && <div className="memo-field"><span className="memo-field-label">CC:</span> {memo.cc}</div>}
          </div>

          <hr className="memo-divider" />

          <div className="memo-body">
            {memo.sections.map((section, i) => {
              if (section.type === 'heading') {
                return <h2 key={i} className="memo-section-heading">{section.content}</h2>;
              }
              return <p key={i}>{renderContent(section.content, memo.footnotes)}</p>;
            })}
          </div>

          <div className="memo-pdf-link">
            <a
              href={`${process.env.PUBLIC_URL}/memos/${memo.filename}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View original PDF
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export { MemosList, MemoViewer };
